import { useEffect, useRef, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";
import { formatFileSize } from "../lib/attachments";
import type { AttachmentNotice } from "../hooks/useAttachments";
import type { ComposerAttachment } from "../types/chat";

interface AttachmentPreviewsProps {
  attachments: ComposerAttachment[];
  onRemove: (id: string) => void;
  onRetry?: (id: string) => void;
  notice?: AttachmentNotice | null;
  style?: CSSProperties;
}

const styles: Record<string, CSSProperties> = {
  strip: {
    display: "flex",
    gap: tokens.space[2],
    overflowX: "auto",
    overflowY: "hidden",
    overscrollBehaviorX: "contain",
    touchAction: "pan-x",
    WebkitOverflowScrolling: "touch",
    paddingTop: 2,
    // Gap between thumbnails and the scroll indicator.
    paddingBottom: 10,
  },
  item: {
    position: "relative",
    flexShrink: 0,
    width: 64,
    height: 64,
  },
  thumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    borderRadius: tokens.radius.md,
    border: `1px solid ${tokens.border.default}`,
    boxSizing: "border-box",
    background: tokens.surface.chip,
  },
  // PDF tile: no thumbnail, just the name and size.
  doc: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    gap: 2,
    padding: "6px",
    borderRadius: tokens.radius.md,
    border: `1px solid ${tokens.border.default}`,
    boxSizing: "border-box",
    background: tokens.surface.chip,
    color: tokens.text.primary,
    fontSize: 10,
    lineHeight: 1.2,
  },
  docBadge: {
    alignSelf: "flex-start",
    padding: "1px 4px",
    borderRadius: 3,
    background: tokens.color.danger[700],
    color: "#fff",
    fontWeight: tokens.font.weight.semibold,
    fontSize: 9,
  },
  docName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  docSize: {
    color: tokens.text.muted,
  },
  // Covers the tile while uploading or after a failure.
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.radius.md,
    background: "rgba(255, 255, 255, 0.72)",
    color: tokens.text.primary,
    fontSize: 11,
    fontWeight: tokens.font.weight.semibold,
    border: "none",
    padding: 4,
    textAlign: "center",
    fontFamily: "inherit",
  },
  overlayFailed: {
    background: "rgba(255, 255, 255, 0.85)",
    color: tokens.color.danger[700],
    cursor: "pointer",
  },
  remove: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "none",
    padding: 0,
    background: "rgba(255, 255, 255, 0.92)",
    color: tokens.text.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  },
  notice: {
    margin: "0 0 8px",
    fontSize: tokens.font.size.xs,
    lineHeight: 1.4,
    color: tokens.text.danger,
  },
};

export default function AttachmentPreviews({
  attachments,
  onRemove,
  onRetry,
  notice,
  style,
}: AttachmentPreviewsProps) {
  const { t } = useTranslation();
  const stripRef = useRef<HTMLDivElement>(null);
  const hasAttachments = attachments.length > 0;

  // Mouse wheels only scroll vertically — map that onto the horizontal strip.
  // Native listener: React's onWheel is passive and can't preventDefault.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const atStart = el.scrollLeft <= 0 && event.deltaY < 0;
      const atEnd = el.scrollLeft >= max - 1 && event.deltaY > 0;
      if (atStart || atEnd) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [hasAttachments]);

  // Newly added files land off-screen on the right — bring them into view.
  const countRef = useRef(attachments.length);
  useEffect(() => {
    const el = stripRef.current;
    if (el && attachments.length > countRef.current) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    }
    countRef.current = attachments.length;
  }, [attachments.length]);

  if (!hasAttachments && !notice) return null;

  return (
    <div style={style}>
      {notice && (
        <p style={styles.notice} role="alert">
          {t(`attachment.${notice.key}`, { name: notice.name ?? "" })}
        </p>
      )}
      {hasAttachments && (
        <div
          ref={stripRef}
          style={styles.strip}
          aria-label={t("input.attachments")}
          data-attachment-strip=""
        >
          {attachments.map((attachment, index) => (
            <div key={attachment.id} style={styles.item}>
              {attachment.kind === "image" && attachment.previewUrl ? (
                <img
                  src={attachment.previewUrl}
                  alt={attachment.file.name}
                  style={styles.thumb}
                  draggable={false}
                />
              ) : (
                <div style={styles.doc} title={attachment.file.name}>
                  <span style={styles.docBadge}>PDF</span>
                  <span style={styles.docName}>{attachment.file.name}</span>
                  <span style={styles.docSize}>
                    {formatFileSize(attachment.file.size)}
                  </span>
                </div>
              )}
              {attachment.status === "uploading" && (
                <div
                  style={styles.overlay}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(attachment.progress * 100)}
                  aria-label={t("input.uploading", {
                    percent: Math.round(attachment.progress * 100),
                  })}
                >
                  {Math.round(attachment.progress * 100)}%
                </div>
              )}
              {attachment.status === "failed" && (
                <button
                  type="button"
                  style={{ ...styles.overlay, ...styles.overlayFailed }}
                  onClick={() => onRetry?.(attachment.id)}
                  aria-label={t("input.retryUpload", {
                    name: attachment.file.name,
                  })}
                  title={t("input.uploadFailed")}
                >
                  ↻
                </button>
              )}
              <button
                type="button"
                style={styles.remove}
                onClick={() => onRemove(attachment.id)}
                aria-label={t("input.removeAttachment", { index: index + 1 })}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <path
                    d="M1.5 1.5l7 7M8.5 1.5l-7 7"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
